import { useState } from 'react';
import TextInputField from 'components/TextInputField';
import ActionButton from 'components/ActionButton';
import StatusMessage from 'components/StatusMessage';
import { FormControl, Select, MenuItem } from '@mui/material';
import { useSaveAddressMutation } from 'store/services/auth/authApi';
import { useGetMeQuery } from 'store/services/user/meApi';
import { trackEvent, getUserProperties } from 'utils/analytics';
import '../components/LoginForm/forms/JoinWaitlistTab/JoinWaitlistTab.css';

type Props = {
  onNext: () => void;
};

const AddressStep = ({ onNext }: Props) => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
  });
  const [saveAddress, { isLoading, error }] = useSaveAddressMutation();
  const { data: userData } = useGetMeQuery();

  const isValid =
    form.firstName &&
    form.lastName &&
    form.address1 &&
    form.city &&
    form.state &&
    form.postalCode &&
    form.country;

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFinish = async () => {
    if (!isValid) return;

    try {
      const addressData = {
        firstName: form.firstName,
        lastName: form.lastName,
        address1: form.address1,
        city: form.city,
        state: form.state,
        postalCode: form.postalCode,
        country: form.country,
        ...(form.address2 && { address2: form.address2 }), // Only include if not empty
      };

      await saveAddress(addressData).unwrap();

      // Track shipping address added in GA4
      trackEvent('shipping_address_added', {
        ...getUserProperties({
          id: userData?.id,
          createdAt: userData?.createdAt,
          country: form.country, // Use country from the form
        }),
        country: form.country, // Override with selected country
      });

      // Longer delay to ensure cookies are fully set after address save
      // This is critical for cookie authentication on staging/prod
      // Increased from 100ms to 300ms for better reliability
      await new Promise(resolve => setTimeout(resolve, 300));

      onNext();
    } catch (err: any) {
      // Log error without sensitive details
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to save address - Status:', err?.status);
      }
      // Error is handled by the error state from the mutation
    }
  };

  return (
    <div className="login-form wide">
      <div className="login-form-content join-waitlist">
        <div className="onboarding-progress-dots">
          <div className="dot" />
          <div className="dot" />
          <div className="dot" />
          <div className="dot active" />
        </div>

        <h1 className="join-waitlist-title">Shipping address</h1>
        <p className="join-waitlist-description" style={{ marginTop: 0 }}>
          Members who mail you cards will send them to this location.
        </p>

        <div className="join-waitlist-input">
          <>
            <div className="onboarding-row">
              <TextInputField
                placeholder="First Name"
                value={form.firstName}
                onChange={(val) => handleChange('firstName', val)}
              />
              <TextInputField
                placeholder="Last Name"
                value={form.lastName}
                onChange={(val) => handleChange('lastName', val)}
              />
            </div>
            
            <TextInputField
              placeholder="Address Line 1"
              value={form.address1}
              onChange={(val) => handleChange('address1', val)}
            />
            <TextInputField
              placeholder="Address Line 2"
              value={form.address2}
              onChange={(val) => handleChange('address2', val)}
            />

            <div className="onboarding-row">
              <TextInputField
                placeholder="City"
                value={form.city}
                onChange={(val) => handleChange('city', val)}
              />
              <TextInputField
                placeholder="State / Province / Region"
                value={form.state}
                onChange={(val) => handleChange('state', val)}
              />
            </div>

            <div className="onboarding-row">
              <TextInputField
                placeholder="Postal Code"
                value={form.postalCode}
                onChange={(val) => handleChange('postalCode', val)}
              />
              <FormControl fullWidth>
                <Select
                  value={form.country}
                  onChange={(e) => handleChange('country', e.target.value)}
                  displayEmpty
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: '#181818',
                        color: 'white',
                        fontFamily: 'Plus Jakarta Sans',
                        fontSize: '11px',
                        borderRadius: '8px',
                        border: '1px solid #323232',
                      },
                    },
                  }}
                  sx={{
                    height: '50px',
                    borderRadius: '8px',
                    backgroundColor: '#181818',
                    color: 'white',
                    fontFamily: 'Plus Jakarta Sans',
                    fontSize: '11px',
                    border: '1px solid #323232',
                    '& .MuiSelect-icon': {
                      color: 'white',
                    },
                  }}
                  inputProps={{ sx: { padding: '0 14px' } }}
                >
                  <MenuItem value="" sx={{ fontStyle: 'italic', color: 'gray' }}>
                    Country
                  </MenuItem>
                  <MenuItem value="US">United States</MenuItem>
                  <MenuItem value="AF">Afghanistan</MenuItem>
                  <MenuItem value="AL">Albania</MenuItem>
                  <MenuItem value="DZ">Algeria</MenuItem>
                  <MenuItem value="AD">Andorra</MenuItem>
                  <MenuItem value="AO">Angola</MenuItem>
                  <MenuItem value="AG">Antigua and Barbuda</MenuItem>
                  <MenuItem value="AR">Argentina</MenuItem>
                  <MenuItem value="AM">Armenia</MenuItem>
                  <MenuItem value="AU">Australia</MenuItem>
                  <MenuItem value="AT">Austria</MenuItem>
                  <MenuItem value="AZ">Azerbaijan</MenuItem>
                  <MenuItem value="BS">Bahamas</MenuItem>
                  <MenuItem value="BH">Bahrain</MenuItem>
                  <MenuItem value="BD">Bangladesh</MenuItem>
                  <MenuItem value="BB">Barbados</MenuItem>
                  <MenuItem value="BY">Belarus</MenuItem>
                  <MenuItem value="BE">Belgium</MenuItem>
                  <MenuItem value="BZ">Belize</MenuItem>
                  <MenuItem value="BJ">Benin</MenuItem>
                  <MenuItem value="BT">Bhutan</MenuItem>
                  <MenuItem value="BO">Bolivia</MenuItem>
                  <MenuItem value="BA">Bosnia and Herzegovina</MenuItem>
                  <MenuItem value="BW">Botswana</MenuItem>
                  <MenuItem value="BR">Brazil</MenuItem>
                  <MenuItem value="BN">Brunei</MenuItem>
                  <MenuItem value="BG">Bulgaria</MenuItem>
                  <MenuItem value="BF">Burkina Faso</MenuItem>
                  <MenuItem value="BI">Burundi</MenuItem>
                  <MenuItem value="KH">Cambodia</MenuItem>
                  <MenuItem value="CM">Cameroon</MenuItem>
                  <MenuItem value="CA">Canada</MenuItem>
                  <MenuItem value="CV">Cape Verde</MenuItem>
                  <MenuItem value="CF">Central African Republic</MenuItem>
                  <MenuItem value="TD">Chad</MenuItem>
                  <MenuItem value="CL">Chile</MenuItem>
                  <MenuItem value="CN">China</MenuItem>
                  <MenuItem value="CO">Colombia</MenuItem>
                  <MenuItem value="KM">Comoros</MenuItem>
                  <MenuItem value="CD">Congo (DRC)</MenuItem>
                  <MenuItem value="CG">Congo (Republic)</MenuItem>
                  <MenuItem value="CR">Costa Rica</MenuItem>
                  <MenuItem value="HR">Croatia</MenuItem>
                  <MenuItem value="CU">Cuba</MenuItem>
                  <MenuItem value="CY">Cyprus</MenuItem>
                  <MenuItem value="CZ">Czech Republic</MenuItem>
                  <MenuItem value="DK">Denmark</MenuItem>
                  <MenuItem value="DJ">Djibouti</MenuItem>
                  <MenuItem value="DM">Dominica</MenuItem>
                  <MenuItem value="DO">Dominican Republic</MenuItem>
                  <MenuItem value="EC">Ecuador</MenuItem>
                  <MenuItem value="EG">Egypt</MenuItem>
                  <MenuItem value="SV">El Salvador</MenuItem>
                  <MenuItem value="GQ">Equatorial Guinea</MenuItem>
                  <MenuItem value="ER">Eritrea</MenuItem>
                  <MenuItem value="EE">Estonia</MenuItem>
                  <MenuItem value="SZ">Eswatini</MenuItem>
                  <MenuItem value="ET">Ethiopia</MenuItem>
                  <MenuItem value="FJ">Fiji</MenuItem>
                  <MenuItem value="FI">Finland</MenuItem>
                  <MenuItem value="FR">France</MenuItem>
                  <MenuItem value="GA">Gabon</MenuItem>
                  <MenuItem value="GM">Gambia</MenuItem>
                  <MenuItem value="GE">Georgia</MenuItem>
                  <MenuItem value="DE">Germany</MenuItem>
                  <MenuItem value="GH">Ghana</MenuItem>
                  <MenuItem value="GR">Greece</MenuItem>
                  <MenuItem value="GD">Grenada</MenuItem>
                  <MenuItem value="GT">Guatemala</MenuItem>
                  <MenuItem value="GN">Guinea</MenuItem>
                  <MenuItem value="GW">Guinea-Bissau</MenuItem>
                  <MenuItem value="GY">Guyana</MenuItem>
                  <MenuItem value="HT">Haiti</MenuItem>
                  <MenuItem value="HN">Honduras</MenuItem>
                  <MenuItem value="HU">Hungary</MenuItem>
                  <MenuItem value="IS">Iceland</MenuItem>
                  <MenuItem value="IN">India</MenuItem>
                  <MenuItem value="ID">Indonesia</MenuItem>
                  <MenuItem value="IR">Iran</MenuItem>
                  <MenuItem value="IQ">Iraq</MenuItem>
                  <MenuItem value="IE">Ireland</MenuItem>
                  <MenuItem value="IL">Israel</MenuItem>
                  <MenuItem value="IT">Italy</MenuItem>
                  <MenuItem value="CI">Ivory Coast</MenuItem>
                  <MenuItem value="JM">Jamaica</MenuItem>
                  <MenuItem value="JP">Japan</MenuItem>
                  <MenuItem value="JO">Jordan</MenuItem>
                  <MenuItem value="KZ">Kazakhstan</MenuItem>
                  <MenuItem value="KE">Kenya</MenuItem>
                  <MenuItem value="KI">Kiribati</MenuItem>
                  <MenuItem value="KW">Kuwait</MenuItem>
                  <MenuItem value="KG">Kyrgyzstan</MenuItem>
                  <MenuItem value="LA">Laos</MenuItem>
                  <MenuItem value="LV">Latvia</MenuItem>
                  <MenuItem value="LB">Lebanon</MenuItem>
                  <MenuItem value="LS">Lesotho</MenuItem>
                  <MenuItem value="LR">Liberia</MenuItem>
                  <MenuItem value="LY">Libya</MenuItem>
                  <MenuItem value="LI">Liechtenstein</MenuItem>
                  <MenuItem value="LT">Lithuania</MenuItem>
                  <MenuItem value="LU">Luxembourg</MenuItem>
                  <MenuItem value="MG">Madagascar</MenuItem>
                  <MenuItem value="MW">Malawi</MenuItem>
                  <MenuItem value="MY">Malaysia</MenuItem>
                  <MenuItem value="MV">Maldives</MenuItem>
                  <MenuItem value="ML">Mali</MenuItem>
                  <MenuItem value="MT">Malta</MenuItem>
                  <MenuItem value="MH">Marshall Islands</MenuItem>
                  <MenuItem value="MR">Mauritania</MenuItem>
                  <MenuItem value="MU">Mauritius</MenuItem>
                  <MenuItem value="MX">Mexico</MenuItem>
                  <MenuItem value="FM">Micronesia</MenuItem>
                  <MenuItem value="MD">Moldova</MenuItem>
                  <MenuItem value="MC">Monaco</MenuItem>
                  <MenuItem value="MN">Mongolia</MenuItem>
                  <MenuItem value="ME">Montenegro</MenuItem>
                  <MenuItem value="MA">Morocco</MenuItem>
                  <MenuItem value="MZ">Mozambique</MenuItem>
                  <MenuItem value="MM">Myanmar</MenuItem>
                  <MenuItem value="NA">Namibia</MenuItem>
                  <MenuItem value="NR">Nauru</MenuItem>
                  <MenuItem value="NP">Nepal</MenuItem>
                  <MenuItem value="NL">Netherlands</MenuItem>
                  <MenuItem value="NZ">New Zealand</MenuItem>
                  <MenuItem value="NI">Nicaragua</MenuItem>
                  <MenuItem value="NE">Niger</MenuItem>
                  <MenuItem value="NG">Nigeria</MenuItem>
                  <MenuItem value="KP">North Korea</MenuItem>
                  <MenuItem value="MK">North Macedonia</MenuItem>
                  <MenuItem value="NO">Norway</MenuItem>
                  <MenuItem value="OM">Oman</MenuItem>
                  <MenuItem value="PK">Pakistan</MenuItem>
                  <MenuItem value="PW">Palau</MenuItem>
                  <MenuItem value="PA">Panama</MenuItem>
                  <MenuItem value="PG">Papua New Guinea</MenuItem>
                  <MenuItem value="PY">Paraguay</MenuItem>
                  <MenuItem value="PE">Peru</MenuItem>
                  <MenuItem value="PH">Philippines</MenuItem>
                  <MenuItem value="PL">Poland</MenuItem>
                  <MenuItem value="PT">Portugal</MenuItem>
                  <MenuItem value="QA">Qatar</MenuItem>
                  <MenuItem value="RO">Romania</MenuItem>
                  <MenuItem value="RU">Russia</MenuItem>
                  <MenuItem value="RW">Rwanda</MenuItem>
                  <MenuItem value="KN">Saint Kitts and Nevis</MenuItem>
                  <MenuItem value="LC">Saint Lucia</MenuItem>
                  <MenuItem value="VC">Saint Vincent and the Grenadines</MenuItem>
                  <MenuItem value="WS">Samoa</MenuItem>
                  <MenuItem value="SM">San Marino</MenuItem>
                  <MenuItem value="ST">Sao Tome and Principe</MenuItem>
                  <MenuItem value="SA">Saudi Arabia</MenuItem>
                  <MenuItem value="SN">Senegal</MenuItem>
                  <MenuItem value="RS">Serbia</MenuItem>
                  <MenuItem value="SC">Seychelles</MenuItem>
                  <MenuItem value="SL">Sierra Leone</MenuItem>
                  <MenuItem value="SG">Singapore</MenuItem>
                  <MenuItem value="SK">Slovakia</MenuItem>
                  <MenuItem value="SI">Slovenia</MenuItem>
                  <MenuItem value="SB">Solomon Islands</MenuItem>
                  <MenuItem value="SO">Somalia</MenuItem>
                  <MenuItem value="ZA">South Africa</MenuItem>
                  <MenuItem value="KR">South Korea</MenuItem>
                  <MenuItem value="SS">South Sudan</MenuItem>
                  <MenuItem value="ES">Spain</MenuItem>
                  <MenuItem value="LK">Sri Lanka</MenuItem>
                  <MenuItem value="SD">Sudan</MenuItem>
                  <MenuItem value="SR">Suriname</MenuItem>
                  <MenuItem value="SE">Sweden</MenuItem>
                  <MenuItem value="CH">Switzerland</MenuItem>
                  <MenuItem value="SY">Syria</MenuItem>
                  <MenuItem value="TW">Taiwan</MenuItem>
                  <MenuItem value="TJ">Tajikistan</MenuItem>
                  <MenuItem value="TZ">Tanzania</MenuItem>
                  <MenuItem value="TH">Thailand</MenuItem>
                  <MenuItem value="TL">Timor-Leste</MenuItem>
                  <MenuItem value="TG">Togo</MenuItem>
                  <MenuItem value="TO">Tonga</MenuItem>
                  <MenuItem value="TT">Trinidad and Tobago</MenuItem>
                  <MenuItem value="TN">Tunisia</MenuItem>
                  <MenuItem value="TR">Turkey</MenuItem>
                  <MenuItem value="TM">Turkmenistan</MenuItem>
                  <MenuItem value="TV">Tuvalu</MenuItem>
                  <MenuItem value="UG">Uganda</MenuItem>
                  <MenuItem value="UA">Ukraine</MenuItem>
                  <MenuItem value="AE">United Arab Emirates</MenuItem>
                  <MenuItem value="GB">United Kingdom</MenuItem>
                  <MenuItem value="US">United States</MenuItem>
                  <MenuItem value="UY">Uruguay</MenuItem>
                  <MenuItem value="UZ">Uzbekistan</MenuItem>
                  <MenuItem value="VU">Vanuatu</MenuItem>
                  <MenuItem value="VA">Vatican City</MenuItem>
                  <MenuItem value="VE">Venezuela</MenuItem>
                  <MenuItem value="VN">Vietnam</MenuItem>
                  <MenuItem value="YE">Yemen</MenuItem>
                  <MenuItem value="ZM">Zambia</MenuItem>
                  <MenuItem value="ZW">Zimbabwe</MenuItem>
                </Select>
              </FormControl>
            </div>

            <ActionButton
              label="Finish"
              onPress={handleFinish}
              isLoading={isLoading}
              isDisabled={!isValid}
            />
            
            {error && (
              <StatusMessage 
                type="error" 
                message={
                  (error as any).data?.message || 
                  `Failed to save address. Status: ${(error as any).status}. Please try again.`
                } 
              />
            )}
          </>
        </div>
      </div>
    </div>
  );
};

export default AddressStep;
